import { useState, type RefObject } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Column,
  Row,
  Text,
} from "@hightouch/htui";

import { TallFormModal } from "src/components/modals/tall-form-modal";
import { RailItem } from "src/components/asset-picker/asset-picker-primitives";
import {
  FolderRailItem,
  type FolderRailItemProps,
} from "src/components/asset-picker/folder-rail-item";

export type RailSubItem = FolderRailItemProps & { id: string };

/** A selectable source in the left rail (Product catalog, Image library, …). */
export type AssetSource = {
  /** Stable key used as the rail view identifier. */
  id: string;
  label: string;
  icon: React.ElementType;
  /** An image drawn in place of `icon`, such as a brand's logo. */
  iconUrl?: string | null;
  /** Total count shown in the rail badge (unfiltered). Omit when the source has no total. */
  count?: number;
  isLoadingCount?: boolean;
  /** Pane rendered when this source is the active view. */
  pane: React.ReactNode;
  /** Nested rows rendered below this source while it is active. */
  subItems?: RailSubItem[];
  /** Sources this one gathers, always listed below it on an indented rail, such as the types of file in a library. */
  children?: AssetSource[];
};

export type AssetSourceGroup = {
  id: string;
  label: string;
  sources: AssetSource[];
  defaultExpanded?: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  maxWidth?: string;
  sources: AssetSource[];
  /**
   * "multi" (default) shows a "Selected" rail tab above the sources; "single"
   * hides it (and its count badge) and opens on the first source. The shell
   * shows nothing else about the pick; a caller may still echo the count in
   * its footer.
   */
  variant?: "single" | "multi";
  /**
   * Total picked across all sources — drives the "Selected" rail badge.
   * Not rendered in single-select mode, so optional there.
   */
  selectedCount?: number;
  /**
   * Pane rendered for the "Selected" view. Unused in single-select mode.
   * Omit it on a multi-select picker with one source and the rail is dropped
   * entirely: the pane fills the modal and the footer label carries the count.
   */
  selectedPane?: React.ReactNode;
  /** Which source id opens by default. Defaults to the first source. */
  defaultViewId?: string;
  /** Controlled active source id. Omit to keep view state inside the modal. */
  activeViewId?: string;
  onActiveViewChange?: (id: string) => void;
  /** Collapsible source sections rendered after the top-level sources. */
  groups?: AssetSourceGroup[];
  /** The label between the "Selected" view and the sources. */
  sourcesLabel?: string;
  /**
   * Optional right-hand preview panel (e.g. a large preview of the focused
   * item). When provided, the modal splits into rail | grid | preview and
   * widens to fit. Callers own the focused-item state and the panel content.
   */
  previewPanel?: React.ReactNode;
  footer: React.ReactNode;
  /** Where focus goes when the modal closes. Defaults to the element that opened it. */
  finalFocusRef?: RefObject<HTMLElement>;
};

/**
 * The shared asset-picker shell: a left rail with a "Selected" view above the
 * configured sources, and a right pane that switches on the active rail item.
 * The rail only renders when it has more than one entry to switch between.
 * Rides the 80vh TallFormModal with innerScroll so the search bars and
 * result headers stay fixed while each pane's grid scrolls. Callers own the
 * panes, the selection state, and the footer actions.
 */
export const AssetPickerModal = ({
  isOpen,
  onClose,
  title,
  maxWidth,
  sources,
  variant = "multi",
  selectedCount,
  selectedPane,
  defaultViewId,
  activeViewId,
  onActiveViewChange,
  groups = [],
  sourcesLabel = "Sources",
  previewPanel,
  footer,
  finalFocusRef,
}: Props) => {
  const isSingle = variant === "single";
  const hasSelectedView = !isSingle && selectedPane !== undefined;
  const [uncontrolledView, setUncontrolledView] = useState<string>(
    defaultViewId ?? sources[0]?.id ?? groups[0]?.sources[0]?.id ?? "selected",
  );
  const view = activeViewId ?? uncontrolledView;
  const setView = (id: string) => {
    if (activeViewId === undefined) {
      setUncontrolledView(id);
    }
    onActiveViewChange?.(id);
  };

  const allSources = [
    ...sources.flatMap((source) => [source, ...(source.children ?? [])]),
    ...groups.flatMap((group) => group.sources),
  ];
  const activeSource = allSources.find((source) => source.id === view);

  // A rail with one source and no nested navigation is dead chrome, so that
  // picker gets the full width.
  const showRail =
    hasSelectedView ||
    allSources.length > 1 ||
    allSources.some((source) => (source.subItems?.length ?? 0) > 0);

  // Widen to fit the third column when a preview panel is present; otherwise
  // keep the two-column default.
  const resolvedMaxWidth = maxWidth ?? (previewPanel ? "7xl" : "6xl");

  return (
    <TallFormModal
      isOpen={isOpen}
      innerScroll
      maxWidth={resolvedMaxWidth}
      title={title}
      onClose={onClose}
      footer={footer}
      finalFocusRef={finalFocusRef}
    >
      <Box
        display="flex"
        gap={6}
        alignItems="stretch"
        flex={1}
        minHeight={0}
        flexDirection={{ base: "column", lg: "row" }}
        overflowY={{ base: "auto", lg: "visible" }}
      >
        {/* Left rail: the current selection plus the sources. The right border
            reads as a full-height divider between rail and pane — bleed through
            the body's vertical padding (my={-6}, then py={6} back inside) so the
            divider runs from the header border to the footer border. */}
        {showRail && (
          <Column
            gap={1}
            width={{ base: "100%", lg: "xs" }}
            maxHeight={{ base: "3xs", lg: "none" }}
            flexShrink={0}
            pr={{ base: 0, lg: 4 }}
            pb={{ base: 4, lg: 0 }}
            my={{ base: 0, lg: -6 }}
            py={{ base: 0, lg: 6 }}
            overflowY="auto"
            borderRightWidth={{ base: 0, lg: "hairline" }}
            borderRightStyle="solid"
            borderBottomWidth={{ base: "hairline", lg: 0 }}
            borderBottomStyle="solid"
            borderColor="base.border"
          >
            {hasSelectedView && (
              <>
                <RailItem
                  label="Selected"
                  icon={CheckIcon}
                  count={selectedCount ?? 0}
                  isActive={view === "selected"}
                  onClick={() => setView("selected")}
                />
                <Box mt={4} mb={1} px={3}>
                  <Text size="sm" color="text.tertiary" fontWeight="medium">
                    {sourcesLabel}
                  </Text>
                </Box>
              </>
            )}
            {sources.map((source) => (
              <SourceRailItems
                key={source.id}
                source={source}
                view={view}
                onViewChange={setView}
              />
            ))}
            {groups.map((group) => (
              <SourceGroupRail
                key={group.id}
                group={group}
                view={view}
                onViewChange={setView}
              />
            ))}
          </Column>
        )}

        {/* Each pane splits itself into a fixed header and a scrolling grid, so
            this column just hands down the available height. Key the active pane
            on the view so switching sources remounts it: sibling source panes
            can be different components (or the same component wired to a
            different data hook), and swapping one for another in place would
            change the hook order and violate the Rules of Hooks. */}
        <Column
          key={view}
          flex={1}
          minWidth={0}
          minHeight={{ base: previewPanel ? "sm" : 0, lg: 0 }}
        >
          {hasSelectedView && view === "selected"
            ? selectedPane
            : activeSource?.pane}
        </Column>

        {/* Optional preview panel: a fixed-width right column, divided from the
            grid. Bleeds through the modal body's padding (my={-6} vertical,
            mr={-6} right) so the divider runs the full height — from the header
            border to the footer border — and the panel's preview sits flush to
            the divider, the modal's right edge, and the footer border. The
            caller owns its content, its internal padding, and the focused-item
            state. */}
        {previewPanel && (
          <Column
            width={{ base: "100%", lg: "sm" }}
            height={{ base: "sm", lg: "auto" }}
            flexShrink={0}
            minHeight={0}
            my={{ base: 0, lg: -6 }}
            mr={{ base: 0, lg: -6 }}
            borderLeftWidth={{ base: 0, lg: "hairline" }}
            borderLeftStyle="solid"
            borderTopWidth={{ base: "hairline", lg: 0 }}
            borderTopStyle="solid"
            borderColor="base.border"
          >
            {previewPanel}
          </Column>
        )}
      </Box>
    </TallFormModal>
  );
};

const SourceRailItems = ({
  source,
  view,
  onViewChange,
}: {
  source: AssetSource;
  view: string;
  onViewChange: (id: string) => void;
}) => (
  <>
    <RailItem
      label={source.label}
      icon={source.icon}
      iconUrl={source.iconUrl}
      count={source.count}
      isLoading={source.isLoadingCount}
      isActive={view === source.id}
      onClick={() => onViewChange(source.id)}
    />
    {view === source.id &&
      source.subItems?.map((item) => (
        <FolderRailItem
          key={item.id}
          label={item.label}
          depth={item.depth}
          isSelected={item.isSelected}
          onSelect={item.onSelect}
        />
      ))}
    {source.children && source.children.length > 0 && (
      <Column
        gap={1}
        ml={5}
        pl={2}
        borderLeftWidth="1px"
        borderLeftStyle="solid"
        borderColor="base.border"
      >
        {source.children.map((child) => (
          <SourceRailItems
            key={child.id}
            source={child}
            view={view}
            onViewChange={onViewChange}
          />
        ))}
      </Column>
    )}
  </>
);

const SourceGroupRail = ({
  group,
  view,
  onViewChange,
}: {
  group: AssetSourceGroup;
  view: string;
  onViewChange: (id: string) => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(group.defaultExpanded ?? true);
  const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;

  return (
    <Column gap={1}>
      <Row
        as="button"
        type="button"
        align="center"
        gap={2}
        px={3}
        py={2}
        color="text.secondary"
        _hover={{ color: "text.primary" }}
        onClick={() => setIsExpanded((current) => !current)}
        aria-expanded={isExpanded}
      >
        <Box as={ChevronIcon} boxSize={4} flexShrink={0} />
        <Text size="sm" fontWeight="medium" color="inherit">
          {group.label}
        </Text>
      </Row>
      {isExpanded &&
        group.sources.map((source) => (
          <SourceRailItems
            key={source.id}
            source={source}
            view={view}
            onViewChange={onViewChange}
          />
        ))}
    </Column>
  );
};

/** Convenience re-export for the common Cancel/confirm footer shape. */
export const AssetPickerFooter = ({
  onCancel,
  onConfirm,
  confirmLabel,
  isConfirmDisabled,
  isConfirmLoading = false,
  isCancelDisabled = false,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  isConfirmDisabled: boolean;
  /**
   * Spinner on the confirm button for pickers whose confirm does async work
   * (e.g. copying the picked asset into the workspace) before the modal closes.
   */
  isConfirmLoading?: boolean;
  /** Set alongside `isConfirmLoading` when the picker blocks close mid-confirm. */
  isCancelDisabled?: boolean;
}) => (
  <ButtonGroup>
    <Button isDisabled={isCancelDisabled} onClick={onCancel}>
      Cancel
    </Button>
    <Button
      variant="primary"
      isDisabled={isConfirmDisabled}
      isLoading={isConfirmLoading}
      onClick={onConfirm}
    >
      {confirmLabel}
    </Button>
  </ButtonGroup>
);
